/** Lists & Empty States — GridList demos, notification rows, empty state. */
import { useState } from 'react';
import { ArrowRight, Calendar, Clock, BarChart3, Lock, ChevronRight } from 'lucide-react';
import { type Selection, type Key } from 'react-aria-components';
import { useHover, SURFACE, title, body, meta, Section } from '../shared';
import { GridList, type GridListItem } from '@/interfaces/learner/ui';

// ─── Demo data ────────────────────────────────────────────────────────────────

const courseItems: GridListItem[] = [
  {
    id: '1',
    leading: 'L4',
    leadingColor: 'var(--color-info)',
    title: 'Sorting Algorithms',
    subtitle: 'Intro to Algorithms · Lesson 4',
    trailing: '85%',
  },
  {
    id: '2',
    leading: '3',
    leadingColor: 'var(--color-success)',
    title: 'Study Group: Chemistry',
    subtitle: '3 members · Active now',
  },
  {
    id: '3',
    leading: '10a',
    leadingColor: 'var(--color-warning)',
    title: 'Weekly Review Session',
    subtitle: 'Tomorrow at 10:00',
    trailing: 'In 18h',
  },
  {
    id: '4',
    leading: 'OC',
    leadingColor: 'var(--color-accent)',
    title: 'Certificate: Organic Chemistry',
    subtitle: 'Completed 12 Mar 2026',
  },
];

const personalLessonItems: GridListItem[] = [
  {
    id: 'pl-1',
    leading: 'AI',
    leadingColor: 'var(--color-accent)',
    title: 'Introduction to Neural Networks',
    subtitle: 'Generated 2 days ago · 6 steps',
  },
  {
    id: 'pl-2',
    leading: 'DS',
    leadingColor: 'var(--color-info)',
    title: 'Data Structures Refresher',
    subtitle: 'Generated 5 days ago · 8 steps',
  },
  {
    id: 'pl-3',
    leading: 'PY',
    leadingColor: 'var(--color-success)',
    title: 'Python List Comprehensions',
    subtitle: 'Generated 1 week ago · 4 steps',
  },
  {
    id: 'pl-4',
    leading: 'DB',
    leadingColor: 'var(--color-warning)',
    title: 'SQL Joins Deep Dive',
    subtitle: 'Generated 2 weeks ago · 10 steps',
    isDisabled: true,
  },
];

const lessonItems: GridListItem[] = [
  {
    id: 'les-1',
    leading: '1',
    leadingColor: 'var(--color-success)',
    title: 'What is an Algorithm?',
    subtitle: '5 steps · 8 min',
    trailing: '100%',
  },
  {
    id: 'les-2',
    leading: '2',
    leadingColor: 'var(--color-success)',
    title: 'Big O Notation',
    subtitle: '7 steps · 12 min',
    trailing: '100%',
  },
  {
    id: 'les-3',
    leading: '3',
    leadingColor: 'var(--color-info)',
    title: 'Bubble Sort',
    subtitle: '6 steps · 10 min',
    trailing: '60%',
  },
  {
    id: 'les-4',
    leading: '4',
    leadingColor: 'var(--color-text-muted)',
    title: 'Merge Sort',
    subtitle: '8 steps · 15 min',
  },
];

// ─── Notification row ─────────────────────────────────────────────────────────

function NotificationRow({
  title: text,
  time,
  unread,
}: {
  title: string;
  time: string;
  unread?: boolean;
}) {
  const [h, handlers] = useHover();
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        borderRadius: 10,
        cursor: 'pointer',
        transition: 'background 0.15s',
        background: h ? 'rgba(0,0,0,0.025)' : 'transparent',
      }}
      {...handlers}
    >
      <div className="min-w-0 flex-1">
        <p style={{ fontSize: 13, fontWeight: unread ? 580 : 430, color: 'var(--color-text)' }}>
          {text}
        </p>
      </div>
      <span style={{ ...meta, fontSize: 12, whiteSpace: 'nowrap' }}>{time}</span>
      {unread && (
        <span
          className="rounded-full"
          style={{ width: 6, height: 6, background: 'var(--color-accent)', flexShrink: 0 }}
        />
      )}
    </div>
  );
}

// ─── Pill button ──────────────────────────────────────────────────────────────

function PillButton({
  children,
  icon: Icon,
}: {
  children: React.ReactNode;
  icon?: React.ComponentType<{ size?: number; strokeWidth?: number }>;
}) {
  const [h, handlers] = useHover();
  return (
    <button
      type="button"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        padding: '9px 18px',
        borderRadius: 9999,
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'box-shadow 0.15s, transform 0.15s, background 0.15s',
        background: h ? 'var(--color-accent-hover)' : 'var(--color-accent)',
        color: '#fff',
        border: '1px solid transparent',
        boxShadow: h ? '0 2px 6px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)',
        transform: h ? 'translateY(-0.5px)' : undefined,
      }}
      {...handlers}
    >
      {Icon && <Icon size={14} strokeWidth={2} />}
      {children}
    </button>
  );
}

// ─── Setting Row ──────────────────────────────────────────────────────────────

function SettingRow({
  icon: Icon,
  label,
  description,
  toggle,
}: {
  icon: React.ComponentType<{ size?: number; strokeWidth?: number; style?: React.CSSProperties }>;
  label: string;
  description: string;
  toggle?: boolean;
}) {
  const [on, setOn] = useState(toggle ?? false);
  const [h, handlers] = useHover();

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '14px 0',
        cursor: toggle !== undefined ? 'pointer' : 'default',
        transition: 'background 0.15s',
      }}
      onClick={() => toggle !== undefined && setOn(!on)}
      {...handlers}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: h
            ? 'color-mix(in srgb, var(--color-accent) 8%, transparent)'
            : 'var(--color-surface-muted)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: 'background 0.15s',
        }}
      >
        <Icon size={16} strokeWidth={2} style={{ color: 'var(--color-text-secondary)' }} />
      </div>
      <div className="min-w-0 flex-1">
        <p style={{ ...title, fontSize: 14 }}>{label}</p>
        <p style={{ ...meta, marginTop: 1 }}>{description}</p>
      </div>
      {toggle !== undefined && (
        <div
          style={{
            width: 40,
            height: 22,
            borderRadius: 11,
            background: on ? 'var(--color-accent)' : 'var(--color-surface-muted)',
            border: on ? '1px solid var(--color-accent)' : '1px solid var(--color-border)',
            position: 'relative',
            transition: 'background 0.2s, border-color 0.2s',
            flexShrink: 0,
            cursor: 'pointer',
          }}
        >
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: 8,
              background: '#fff',
              position: 'absolute',
              top: 2,
              left: on ? 20 : 2,
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
            }}
          />
        </div>
      )}
      {toggle === undefined && (
        <ChevronRight
          size={14}
          strokeWidth={2}
          style={{ color: 'var(--color-text-muted)', opacity: 0.5, flexShrink: 0 }}
        />
      )}
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div
      className="flex flex-col items-center justify-center"
      style={{
        padding: '48px 20px',
        borderRadius: 14,
        border: '1.5px dashed var(--color-surface-border)',
      }}
    >
      <p
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: 'var(--color-text)',
          letterSpacing: '-0.02em',
          marginBottom: 5,
        }}
      >
        Nothing here yet
      </p>
      <p style={{ ...body, fontSize: 14, marginBottom: 16 }}>
        Enrol in a course to start learning.
      </p>
      <PillButton icon={ArrowRight}>Browse courses</PillButton>
    </div>
  );
}

// ─── Tab content ──────────────────────────────────────────────────────────────

export default function ListsTab() {
  const [selected, setSelected] = useState<Selection>(new Set());

  const selectedCount =
    selected === 'all' ? personalLessonItems.length : (selected as Set<Key>).size;

  return (
    <>
      <Section label="Grid List — Navigation">
        <GridList items={courseItems} onAction={(id) => alert(`Navigate to ${id}`)} />
      </Section>

      <Section label="Grid List — Selection">
        <GridList
          items={personalLessonItems}
          selectionMode="multiple"
          selectedKeys={selected}
          onSelectionChange={setSelected}
        />
        {selectedCount > 0 && (
          <p
            style={{
              ...meta,
              marginTop: 10,
              fontWeight: 600,
              color: 'var(--color-accent-text)',
            }}
          >
            {selectedCount} item{selectedCount !== 1 ? 's' : ''} selected
          </p>
        )}
      </Section>

      <Section label="Grid List — No Surface">
        <GridList
          items={lessonItems}
          surface={false}
          onAction={(id) => alert(`Open lesson ${id}`)}
        />
      </Section>

      <Section label="Notifications">
        <div style={{ ...SURFACE, padding: 20 }}>
          <p
            style={{
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--color-text-muted)',
              marginBottom: 12,
            }}
          >
            Recent Activity
          </p>
          <NotificationRow title="Completed Linear Algebra — Lesson 3" time="2m ago" unread />
          <NotificationRow title="New course: Advanced Statistics" time="1h ago" unread />
          <NotificationRow title="Your streak hit 12 days!" time="3h ago" />
          <NotificationRow title="Quiz score: 94% on Organic Chemistry" time="1d ago" />
        </div>
      </Section>

      <Section label="Settings — Rows">
        <div style={{ ...SURFACE, padding: '4px 20px' }}>
          <SettingRow icon={Calendar} label="Week starts on" description="Monday" />
          <div style={{ height: 1, background: 'var(--color-surface-border)' }} />
          <SettingRow
            icon={Clock}
            label="Daily reminder"
            description="Notify me at 9:00 AM"
            toggle
          />
          <div style={{ height: 1, background: 'var(--color-surface-border)' }} />
          <SettingRow
            icon={BarChart3}
            label="Show streak on profile"
            description="Visible to other learners"
            toggle={false}
          />
          <div style={{ height: 1, background: 'var(--color-surface-border)' }} />
          <SettingRow icon={Lock} label="Privacy" description="Manage data and visibility" />
        </div>
      </Section>

      <Section label="Empty State">
        <EmptyState />
      </Section>
    </>
  );
}
